import React, { useState, useEffect } from 'react';
import './VirtualMallProfile.scss';
import { virtualMallProfileApi, virtualMallProfileEditApi, forgotTransactionPasswordRequestApi } from './VirtualMallApi/api.js';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

export default function VirtualMallProfile() {

    const [profileData, setProfileData] = useState({
        vu_id: '',
        vu_name: '',
        vu_mobile: '',
        vu_email: '',
        vu_referal_id: '',
        vu_role: '',
        vu_district: '',
        vu_corporation: '',
        vu_taluk: '',
        vu_panchayat: '',
        vu_pincode: '',
        vu_bank_name: '',
        vu_account_holder_name: '',
        vu_account_number: '',
        vu_ifsc_code: '',
        vu_branch_name: '',
        vu_transaction_password: '',
        created_at: ''
    });

    const [loading, setLoading] = useState(false);
    const [lockedFields, setLockedFields] = useState({});
    const [forgotTxnLoading, setForgotTxnLoading] = useState(false);

    // Identify all fields that are present in the form to determine if "All Locked"
    const formFields = [
        'vu_name', 'vu_mobile', 'vu_district', 'vu_corporation', 'vu_taluk',
        'vu_panchayat', 'vu_pincode', 'vu_bank_name', 'vu_account_holder_name',
        'vu_account_number', 'vu_ifsc_code', 'vu_branch_name', 'vu_transaction_password'
    ];

    const isAllLocked = formFields.every(field => lockedFields[field]);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                setLoading(true);
                const response = await virtualMallProfileApi();
                console.log(response, 'virtualMallProfileApi');
                if (response.status) {
                    const data = response.data;
                    setProfileData({
                        ...data,
                        vu_name: data.vu_name || '',
                        vu_mobile: data.vu_mobile || '',
                        vu_email: data.vu_email || '',
                        vu_district: data.vu_district || '',
                        vu_corporation: data.vu_corporation || '',
                        vu_taluk: data.vu_taluk || '',
                        vu_panchayat: data.vu_panchayat || '',
                        vu_pincode: data.vu_pincode || '',
                        vu_bank_name: data.vu_bank_name || '',
                        vu_account_holder_name: data.vu_account_holder_name || '',
                        vu_account_number:
                            !data.vu_account_number || /^nil$/i.test(String(data.vu_account_number))
                                ? ''
                                : data.vu_account_number,
                        vu_ifsc_code: data.vu_ifsc_code || '',
                        vu_branch_name: data.vu_branch_name || '',
                        vu_transaction_password: data.vu_transaction_password || ''
                    });

                    // Determine locked fields individually
                    const newLockedFields = {};
                    formFields.forEach(field => {
                        if (data[field] && String(data[field]).trim().length > 0) {
                            newLockedFields[field] = true;
                        }
                    });
                    setLockedFields(newLockedFields);
                }
            } catch (error) {
                console.error(error);
                toast.error("Failed to load profile data");
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, []);

    const handleInputChange = (field) => (e) => {
        if (lockedFields[field]) return;
        setProfileData(prev => ({
            ...prev,
            [field]: e.target.value
        }));
    };

    const handleProfileUpdate = async (e) => {
        e.preventDefault();

        try {
            setLoading(true);
            const payload = {
                ...profileData,
                vu_Ifsc_code: profileData.vu_ifsc_code
            };

            const response = await virtualMallProfileEditApi(payload);
            console.log(response, 'virtualMallProfileEditApi');
            if (response.result === true) {
                // If response.data exists, use it. Otherwise, assume payload contains the new valid state.
                const updatedData = response.data || payload;
                setProfileData(prev => ({ ...prev, ...updatedData }));

                // Update locks for fields that were just successfully updated
                const newLockedFields = { ...lockedFields };
                formFields.forEach(field => {
                    // Check payload (current input value) instead of response.data mostly to be safe immediately
                    const val = updatedData[field];
                    if (val && String(val).trim().length > 0) {
                        newLockedFields[field] = true;
                    }
                });
                setLockedFields(newLockedFields);

                toast.success("Profile updated successfully!");
            } else {
                toast.error(response.message || "Failed to update profile");
            }
        } catch (error) {
            console.error(error);
            const errorMsg = error.response?.data?.message || "An error occurred. Please try again.";
            toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="dashboard-section">
            <div className="section-header-flex">
                <h2>Profile Settings</h2>
                {isAllLocked && <span className="locked-badge">🔒 Profile Locked</span>}
            </div>

            <div className="profile-section">
                <div className="profile-header">
                    <div className="profile-avatar">
                        <span className="avatar-icon">👤</span>
                    </div>
                    <div className="profile-info">
                        <h3>{profileData.vu_name || 'Virtual User'}</h3>
                        <p>{profileData.vu_referal_id}</p>
                        <div className="profile-badges">
                            <span className="profile-badge">Joined: {profileData.created_at ? new Date(profileData.created_at).toLocaleDateString() : 'N/A'}</span>
                            <span className="profile-badge status">Role: {profileData.vu_role || 'User'}</span>
                        </div>
                    </div>
                </div>

                <form className="profile-form" onSubmit={handleProfileUpdate}>
                    {/* Personal Details */}
                    <h4 className="form-section-title">Personal Details</h4>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Name</label>
                            <input
                                type="text"
                                value={profileData.vu_name}
                                onChange={handleInputChange('vu_name')}
                                className="form-input"
                                disabled={lockedFields.vu_name}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Mobile Number</label>
                            <input
                                type="tel"
                                value={profileData.vu_mobile}
                                onChange={handleInputChange('vu_mobile')}
                                className="form-input"
                                disabled={lockedFields.vu_mobile}
                                required
                            />
                        </div>
                    </div>

                    {/* Address Details */}
                    <h4 className="form-section-title">Address Details</h4>
                    <div className="form-row">
                        <div className="form-group">
                            <label>District</label>
                            <input
                                type="text"
                                value={profileData.vu_district}
                                onChange={handleInputChange('vu_district')}
                                className="form-input"
                                disabled={lockedFields.vu_district}
                            />
                        </div>
                        <div className="form-group">
                            <label>Corporation</label>
                            <input
                                type="text"
                                value={profileData.vu_corporation}
                                onChange={handleInputChange('vu_corporation')}
                                className="form-input"
                                disabled={lockedFields.vu_corporation}
                            />
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Taluk</label>
                            <input
                                type="text"
                                value={profileData.vu_taluk}
                                onChange={handleInputChange('vu_taluk')}
                                className="form-input"
                                disabled={lockedFields.vu_taluk}
                            />
                        </div>
                        <div className="form-group">
                            <label>Panchayat</label>
                            <input
                                type="text"
                                value={profileData.vu_panchayat}
                                onChange={handleInputChange('vu_panchayat')}
                                className="form-input"
                                disabled={lockedFields.vu_panchayat}
                            />
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Pincode</label>
                            <input
                                type="text"
                                value={profileData.vu_pincode}
                                onChange={handleInputChange('vu_pincode')}
                                className="form-input"
                                disabled={lockedFields.vu_pincode}
                            />
                        </div>
                    </div>

                    {/* Bank Details */}
                    <h4 className="form-section-title">Bank Details</h4>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Bank Name</label>
                            <input
                                type="text"
                                value={profileData.vu_bank_name}
                                onChange={handleInputChange('vu_bank_name')}
                                className="form-input"
                                disabled={lockedFields.vu_bank_name}
                            />
                        </div>
                        <div className="form-group">
                            <label>Account Holder Name</label>
                            <input
                                type="text"
                                value={profileData.vu_account_holder_name}
                                onChange={handleInputChange('vu_account_holder_name')}
                                className="form-input"
                                disabled={lockedFields.vu_account_holder_name}
                            />
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Account Number</label>
                            <input
                                type="text"
                                value={profileData.vu_account_number}
                                onChange={handleInputChange('vu_account_number')}
                                className="form-input"
                                disabled={lockedFields.vu_account_number}
                            />
                        </div>
                        <div className="form-group">
                            <label>IFSC Code</label>
                            <input
                                type="text"
                                value={profileData.vu_ifsc_code}
                                onChange={handleInputChange('vu_ifsc_code')}
                                className="form-input"
                                disabled={lockedFields.vu_ifsc_code}
                            />
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Branch Name</label>
                            <input
                                type="text"
                                value={profileData.vu_branch_name}
                                onChange={handleInputChange('vu_branch_name')}
                                className="form-input"
                                disabled={lockedFields.vu_branch_name}
                            />
                        </div>
                    </div>

                    {/* Security Details */}
                    <h4 className="form-section-title">Security Details</h4>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Transaction Password</label>
                            <input
                                type="password"
                                value={profileData.vu_transaction_password}
                                onChange={handleInputChange('vu_transaction_password')}
                                className="form-input"
                                disabled={lockedFields.vu_transaction_password}
                                placeholder="Set your transaction password"
                            />
                            <button
                                type="button"
                                className="forgot-txn-btn"
                                disabled={forgotTxnLoading}
                                onClick={async () => {
                                    const result = await Swal.fire({
                                        title: 'Request Password Reset?',
                                        text: "Are you sure you want to request a transaction password reset? The admin will set a new password for you.",
                                        icon: 'question',
                                        showCancelButton: true,
                                        confirmButtonColor: '#667eea',
                                        cancelButtonColor: '#d33',
                                        confirmButtonText: 'Yes, request it!'
                                    });
                                    
                                    if (!result.isConfirmed) {
                                        return;
                                    }
                                    
                                    try {
                                        setForgotTxnLoading(true);
                                        const res = await forgotTransactionPasswordRequestApi();
                                        if (res && res.status) {
                                            toast.success(res.message || 'Request submitted. Admin will reset your transaction password shortly.');
                                        } else {
                                            toast.error((res && res.message) || 'Failed to submit request.');
                                        }
                                    } catch (err) {
                                        toast.error('Failed to submit request. Please try again.');
                                    } finally {
                                        setForgotTxnLoading(false);
                                    }
                                }}
                            >
                                {forgotTxnLoading ? 'Submitting...' : 'Forgot Transaction Password?'}
                            </button>
                        </div>
                    </div>

                    <div className="form-actions">
                        <div className="d-flex align-items-center gap-3">
                            <button type="submit" className="primary-btn" disabled={loading || isAllLocked}>
                                {loading ? 'Updating...' : 'Update Profile'}
                            </button>
                            {Object.keys(lockedFields).length > 0 && (
                                <small className="text-muted fst-italic">
                                    * Previously saved fields cannot be edited
                                </small>
                            )}
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}
